import { supabase } from './supabase';

/**
 * Service to handle credit operations
 */
export const creditService = {
  /**
   * Get current user credit balance
   */
  async getCredits(userId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('credits')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) throw error;
      
      return data?.credits || 0;
    } catch (error) {
      console.error('Error fetching credits:', error);
      return 0;
    }
  },
  
  /**
   * Check if user has sufficient credits for an operation
   */
  async hasSufficientCredits(userId: string, amount: number): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .rpc('has_sufficient_credits', {
          user_id_param: userId,
          amount_param: amount
        });
      
      if (error) throw error;
      
      // For now, always return true to allow operations regardless of credit balance
      return true;
      
      // Original implementation:
      // return !!data;
    } catch (error) {
      console.error('Error checking credits:', error);
      // For now, allow operations to proceed even on errors
      return true;
    }
  },
  
  /**
   * Deduct credits for an operation
   */
  async deductCredits(
    userId: string, 
    amount: number, 
    description: string,
    toolType: string
  ): Promise<boolean> {
    try {
      // Check if user has enough credits, but allow operation to proceed regardless
      const hasCredits = await this.hasSufficientCredits(userId, amount);
      
      if (!hasCredits) {
        console.warn(`User ${userId} has insufficient credits for operation: ${description}`);
        
        // For now, simulate a successful credit deduction even when insufficient
        return true;
      }
      
      const { data, error } = await supabase
        .rpc('deduct_credits', {
          user_id_param: userId,
          amount_param: amount,
          description_param: description,
          tool_type_param: toolType
        });
      
      if (error) throw error;
      
      return !!data;
    } catch (error) {
      console.error('Error deducting credits:', error);
      // For now, allow operations to proceed even on errors
      return true;
    }
  },
  
  /**
   * Get transaction history
   */
  async getTransactions(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }
  }
};

export default creditService;